import type {
  AppRole,
  LeadType,
  OperationAdministrativeStatus,
  OperationSemaphore,
  OperationType,
} from "../types";
import type {
  AdvisorCommissionLevel,
  AdvisorLevelCode,
  AdvisorLevelOverride,
  PayableStatus,
  ReceivableStatus,
} from "./invoicingTypes";

/**
 * Almikia Epic E6 (TASK-039): the pure invoicing rules, shared by the FakeRest
 * provider, the demo data and (later) the screens. They mirror the SQL of
 * TASK-038 (`render_invoice_concept`, `advisor_commission_rate`, the money
 * rounding and the transition/role table of the E6 CONTRACT).
 *
 * Money is NEVER float math: amounts are converted to integer cents and
 * percentages to scaled integers, the products are done in BigInt and rounded
 * half-up once, like `round(numeric, 2)` in PostgreSQL.
 */

// ---------------------------------------------------------------- money

const PCT_SCALE = 10_000; // percentages carry up to 4 decimals
const IVA_SCALE = 100; // iva_pct is numeric(5,2)

/** Integer cents of a decimal amount (the amount has at most two decimals). */
export const toCents = (amount: number | string): number =>
  Math.round(Number(amount) * 100);

const fromCents = (cents: bigint): number => Number(cents) / 100;

/** Half-up division for non-negative operands. */
const roundDiv = (numerator: bigint, denominator: bigint): bigint =>
  (numerator * 2n + denominator) / (denominator * 2n);

/** `amount x pct / 100` rounded to cents (pct up to 4 decimals). */
export const percentOfAmount = (
  amount: number | string,
  pct: number | string,
): number =>
  fromCents(
    roundDiv(
      BigInt(toCents(amount)) * BigInt(Math.round(Number(pct) * PCT_SCALE)),
      BigInt(PCT_SCALE * 100),
    ),
  );

export type MoneyBreakdown = {
  subtotal: number;
  iva_amount: number;
  total_amount: number;
};

/** IVA and total of a subtotal: `total = subtotal + iva`, exactly, in cents. */
export const computeMoneyBreakdown = (
  subtotal: number | string,
  ivaPct: number | string,
): MoneyBreakdown => {
  const subtotalCents = BigInt(toCents(subtotal));
  const ivaCents = roundDiv(
    subtotalCents * BigInt(Math.round(Number(ivaPct) * IVA_SCALE)),
    BigInt(IVA_SCALE * 100),
  );
  return {
    subtotal: fromCents(subtotalCents),
    iva_amount: fromCents(ivaCents),
    total_amount: fromCents(subtotalCents + ivaCents),
  };
};

/** Subtotal = base x pct / 100, then IVA and total. */
export const computeCommissionBreakdown = (
  base: number | string,
  pct: number | string,
  ivaPct: number | string,
): MoneyBreakdown => computeMoneyBreakdown(percentOfAmount(base, pct), ivaPct);

// ------------------------------------------------------- invoice concept

const ORDINAL_SUFFIXES = [
  "1RA",
  "2DA",
  "3RA",
  "4TA",
  "5TA",
  "6TA",
  "7MA",
  "8VA",
  "9NA",
  "10MA",
];

/** Installment label of `{N}`: 1RA, 2DA, 3RA, 4TA... (`11A`, `12A` beyond ten). */
export const ordinalLabel = (installment: number): string =>
  ORDINAL_SUFFIXES[installment - 1] ?? `${installment}A`;

/** `{PCT}`: the percentage without trailing zeros (`5%`, `1.5%`). */
export const formatConceptPercent = (pct: number): string =>
  `${Number(pct.toFixed(4))}%`;

export type InvoiceConceptInput = {
  installment: number;
  pct?: number | null;
  unit?: string | null;
  client?: string | null;
  advisor?: string | null;
  project?: string | null;
};

/**
 * `render_invoice_concept`: replaces `{N}`, `{PCT}`, `{UNIT}`, `{CLIENT}`,
 * `{ADVISOR}` and `{PROJECT}`, then UPPERCASES the whole text keeping accents.
 */
export const renderInvoiceConcept = (
  template: string,
  input: InvoiceConceptInput,
): string =>
  template
    .replaceAll("{N}", ordinalLabel(input.installment))
    .replaceAll(
      "{PCT}",
      input.pct == null ? "" : formatConceptPercent(input.pct),
    )
    .replaceAll("{UNIT}", input.unit ?? "")
    .replaceAll("{CLIENT}", input.client ?? "")
    .replaceAll("{ADVISOR}", input.advisor ?? "")
    .replaceAll("{PROJECT}", input.project ?? "")
    .toUpperCase();

// ------------------------------------------------------ commission levels

/** The five levels of product-rules.md "Commissions" (same rows as the SQL seed). */
export const DEFAULT_COMMISSION_LEVELS: ReadonlyArray<
  Omit<AdvisorCommissionLevel, "id">
> = [
  {
    code: "entrenamiento",
    name: "Entrenamiento",
    min_sales: 0,
    max_sales: 2,
    proactivo_pct: 1.5,
    proactivo_step_pct: 0,
    proactivo_cap_pct: null,
    bpo_pct: 1,
    bpo_step_pct: 0,
    bpo_cap_pct: null,
    with_colega_pct: 1,
    valid_from: "2026-01-01",
    valid_to: null,
  },
  {
    code: "junior",
    name: "Junior",
    min_sales: 3,
    max_sales: 11,
    proactivo_pct: 1.5,
    proactivo_step_pct: 0,
    proactivo_cap_pct: null,
    bpo_pct: 1,
    bpo_step_pct: 0,
    bpo_cap_pct: null,
    with_colega_pct: 1,
    valid_from: "2026-01-01",
    valid_to: null,
  },
  {
    code: "senior",
    name: "Senior",
    min_sales: 12,
    max_sales: 21,
    proactivo_pct: 1.55,
    proactivo_step_pct: 0.02,
    proactivo_cap_pct: 1.73,
    bpo_pct: 1.05,
    bpo_step_pct: 0.02,
    bpo_cap_pct: 1.23,
    with_colega_pct: 1,
    valid_from: "2026-01-01",
    valid_to: null,
  },
  {
    code: "elite",
    name: "Élite",
    min_sales: 22,
    max_sales: null,
    proactivo_pct: 1.8,
    proactivo_step_pct: 0,
    proactivo_cap_pct: null,
    bpo_pct: 1.3,
    bpo_step_pct: 0,
    bpo_cap_pct: null,
    with_colega_pct: 1,
    valid_from: "2026-01-01",
    valid_to: null,
  },
  {
    code: "team_leader",
    name: "Team Leader",
    min_sales: null,
    max_sales: null,
    proactivo_pct: 2,
    proactivo_step_pct: 0,
    proactivo_cap_pct: null,
    bpo_pct: 1.5,
    bpo_step_pct: 0,
    bpo_cap_pct: null,
    with_colega_pct: 1.2,
    valid_from: "2026-01-01",
    valid_to: null,
  },
];

export type CommissionColumn = "proactivo" | "bpo" | "with_colega";

/**
 * Which rate column applies: inter_bpo and codigo_azul sales always use the BPO
 * rate; otherwise proactivo -> proactivo, colega -> with colega, the rest -> BPO.
 */
export const commissionColumnFor = (
  leadType: LeadType | null | undefined,
  operationType: OperationType | null | undefined,
): CommissionColumn => {
  if (operationType != null && operationType !== "propia") return "bpo";
  if (leadType === "proactivo") return "proactivo";
  if (leadType === "colega") return "with_colega";
  return "bpo";
};

const scaledPct = (value: number) => Math.round(value * PCT_SCALE);

/**
 * The rate of `level` in `column` after `accumulatedSales` sales: the base rate,
 * plus `step` per sale beyond the level's `min_sales`, capped (Senior).
 */
export const levelRate = (
  level: Omit<AdvisorCommissionLevel, "id">,
  column: CommissionColumn,
  accumulatedSales: number,
): number => {
  if (column === "with_colega") return level.with_colega_pct;
  const base = column === "proactivo" ? level.proactivo_pct : level.bpo_pct;
  const step =
    column === "proactivo" ? level.proactivo_step_pct : level.bpo_step_pct;
  const cap =
    column === "proactivo" ? level.proactivo_cap_pct : level.bpo_cap_pct;
  const extra = Math.max(accumulatedSales - (level.min_sales ?? 0), 0);
  let scaled = scaledPct(base) + scaledPct(step) * extra;
  if (step > 0 && cap != null) scaled = Math.min(scaled, scaledPct(cap));
  return scaled / PCT_SCALE;
};

type DatedRow = { valid_from: string; valid_to?: string | null };

/** True when `date` (YYYY-MM-DD) falls in the row's validity window. */
export const isActiveOn = (row: DatedRow, date: string): boolean =>
  row.valid_from <= date && (row.valid_to == null || date <= row.valid_to);

/** The row of each level code active on `date` (the latest `valid_from` wins). */
export const activeLevels = <Level extends Omit<AdvisorCommissionLevel, "id">>(
  levels: readonly Level[],
  date: string,
): Level[] => {
  const byCode = new Map<AdvisorLevelCode, Level>();
  for (const level of levels) {
    if (!isActiveOn(level, date)) continue;
    const current = byCode.get(level.code);
    if (!current || level.valid_from > current.valid_from) {
      byCode.set(level.code, level);
    }
  }
  return [...byCode.values()];
};

export type CommissionSelection = {
  level_code: AdvisorLevelCode;
  rate_pct: number;
  accumulated_sales: number;
  /** True when an override beat the earned rate (the level code stays the earned one). */
  overridden: boolean;
};

/**
 * `advisor_commission_rate`: the level comes from the advisor's sale number,
 * which COUNTS the sale being paid (QE6-A1; 2/3, 11/12 and 21/22 are the
 * boundaries; `accumulatedSales` is that number); the column from the lead
 * type; an active override wins only when its rate is better, without changing
 * the recorded level. Throws when no level is active.
 */
export const selectCommission = (args: {
  levels: readonly Omit<AdvisorCommissionLevel, "id">[];
  accumulatedSales: number;
  leadType?: LeadType | null;
  operationType?: OperationType | null;
  overrides?: readonly Pick<
    AdvisorLevelOverride,
    "level_code" | "valid_from" | "valid_to"
  >[];
  /** YYYY-MM-DD the sale was signed (validity of levels and overrides). */
  onDate: string;
}): CommissionSelection => {
  const { accumulatedSales, onDate } = args;
  const levels = activeLevels(args.levels, onDate);
  const earned = levels.find(
    (level) =>
      level.min_sales != null &&
      accumulatedSales >= level.min_sales &&
      (level.max_sales == null || accumulatedSales <= level.max_sales),
  );
  if (!earned) throw new Error("No hay un nivel de comisión vigente");
  const column = commissionColumnFor(args.leadType, args.operationType);
  const earnedRate = levelRate(earned, column, accumulatedSales);

  let best = earnedRate;
  for (const override of args.overrides ?? []) {
    if (!isActiveOn(override, onDate)) continue;
    const level = levels.find((row) => row.code === override.level_code);
    if (!level) continue;
    best = Math.max(best, levelRate(level, column, accumulatedSales));
  }
  return {
    level_code: earned.code,
    rate_pct: best,
    accumulated_sales: accumulatedSales,
    overridden: best > earnedRate,
  };
};

// -------------------------------------------------------------- green file

/** The "green file": administrative status validated AND semaphore green. */
export const isGreenFile = (operation: {
  administrative_status: OperationAdministrativeStatus;
  semaphore: OperationSemaphore;
}): boolean =>
  operation.administrative_status === "validado" &&
  operation.semaphore === "verde";

// ------------------------------------------------------------ transitions

export type InvoicingTransition<Status extends string> = {
  action: string;
  from: Status;
  to: Status;
  /** Roles allowed to run it (the RPC raises for any other). */
  roles: readonly AppRole[];
};

const DIRECCION: readonly AppRole[] = ["direccion"];
const FINANCE: readonly AppRole[] = ["contabilidad", "direccion"];

/**
 * Receivable transitions of the E6 CONTRACT. A reject needs a reason and returns
 * to the previous step; at `pendiente_vobo` (nothing earlier) it stays there and
 * only records `last_rejection_*`. A cobrada / pagada row has no reject
 * transition (QE6-A6): later corrections are by adjustment.
 */
export const RECEIVABLE_TRANSITIONS: readonly InvoicingTransition<ReceivableStatus>[] =
  [
    {
      action: "approve",
      from: "pendiente_vobo",
      to: "por_facturar",
      roles: DIRECCION,
    },
    {
      action: "invoice",
      from: "por_facturar",
      to: "facturada",
      roles: FINANCE,
    },
    { action: "collect", from: "facturada", to: "cobrada", roles: FINANCE },
    {
      action: "reject",
      from: "pendiente_vobo",
      to: "pendiente_vobo",
      roles: DIRECCION,
    },
    {
      action: "reject",
      from: "por_facturar",
      to: "pendiente_vobo",
      roles: FINANCE,
    },
    { action: "reject", from: "facturada", to: "por_facturar", roles: FINANCE },
  ];

/** Payable transitions of the E6 CONTRACT (same reject semantics). */
export const PAYABLE_TRANSITIONS: readonly InvoicingTransition<PayableStatus>[] =
  [
    {
      action: "approve",
      from: "pendiente_vobo",
      to: "por_pagar",
      roles: DIRECCION,
    },
    { action: "pay", from: "por_pagar", to: "pagada", roles: FINANCE },
    {
      action: "reject",
      from: "pendiente_vobo",
      to: "pendiente_vobo",
      roles: DIRECCION,
    },
    {
      action: "reject",
      from: "por_pagar",
      to: "pendiente_vobo",
      roles: FINANCE,
    },
  ];

/** The transition of `action` from `status`, or undefined when the status does not allow it. */
export const findTransition = <Status extends string>(
  table: readonly InvoicingTransition<Status>[],
  action: string,
  status: Status,
): InvoicingTransition<Status> | undefined =>
  table.find((row) => row.action === action && row.from === status);

/** True when `role` may run `action` on a row currently in `status`. */
export const canRunTransition = <Status extends string>(
  table: readonly InvoicingTransition<Status>[],
  action: string,
  status: Status,
  role: AppRole,
): boolean =>
  findTransition(table, action, status)?.roles.includes(role) ?? false;
